import React from "react";
import { RiskStatistics } from "@sscs/report/ui";
import { ReportDetails } from "@sscs/types";
import { DataTable, Table, Tooltip } from "@sscs/ui";
import {
  ApprovalStatus,
  DisplaySize,
  FileStatus,
  LinkToReport,
  UserAvatar,
} from "Components";
import { FILESTREAM_PURL } from "Constants";
import { PortalFile } from "Types";
import FileActions from "./FileActions";
import FileDetails from "./FileDetails";
import FileName from "./FileName";
import styles from "./fileRow.module.scss";

type FileRowProps = {
  rowIndex: number;
  file: PortalFile;
  focused: boolean;
  deleted: boolean;
  onRowClick: () => void;
};

const FileRow = ({
  rowIndex,
  file,
  focused,
  deleted,
  onRowClick,
}: FileRowProps): React.JSX.Element => {
  const reportDetails: ReportDetails = {
    version: file.id,
    packageName: FILESTREAM_PURL.packageName,
    projectName: FILESTREAM_PURL.projectName,
    isRepro: false,
  };

  return (
    <>
      <Tooltip
        title="File has been deleted by another user"
        disabled={!deleted}
      >
        <DataTable.ExpandedRow
          id={`expand-row-${rowIndex}`}
          disabled={
            file.latest_report_summary?.report_id === undefined || deleted
          }
          focused={focused}
          onClick={onRowClick}
          emphasizeExpanded={false}
          className={deleted ? styles["disabled-row"] : ""}
        >
          <DataTable.ExpandedRow.Summary>
            <Table.Cell>
              <span
                data-test-file-uuid={file.id}
                data-test-id="processing-status"
              >
                <FileStatus
                  status={file.current_status}
                  scanReport={file.latest_report_summary?.scan_report}
                  scanDuration={file.latest_report_summary?.scan_duration}
                />
              </span>
            </Table.Cell>
            <Table.Cell>
              <ApprovalStatus
                status={file.approval_status}
                info={file.approval_information}
              />
            </Table.Cell>
            <Table.Cell>
              <div className={styles["file-info"]}>
                <UserAvatar user={file.user} size="lg" />
                <FileName
                  rowIndex={rowIndex}
                  fileName={file.file_name}
                  reportDetails={reportDetails}
                  daysUntilDeletion={file.days_until_deletion}
                  hasReport={Boolean(file.latest_report_summary)}
                />
              </div>
            </Table.Cell>
            <Table.Cell>
              <RiskStatistics
                risk={file.latest_report_summary?.scan_report.assessments}
                riskCountWrapper={(assessment, category, children) => (
                  <LinkToReport
                    id={`${category}-link-${rowIndex}`}
                    report={{
                      ...reportDetails,
                      riskCategory: category,
                      riskViolations: assessment.violations,
                    }}
                  >
                    {children}
                  </LinkToReport>
                )}
                viewReportWrapper={(children) => (
                  <LinkToReport
                    id={`view-report-link-${rowIndex}`}
                    report={reportDetails}
                  >
                    {children}
                  </LinkToReport>
                )}
              />
            </Table.Cell>
            <Table.Cell>
              <DisplaySize sizeInBytes={file.usage} fractionDigits={0} />
            </Table.Cell>
            <Table.Cell>
              <FileActions rowIndex={rowIndex} file={file} />
            </Table.Cell>
          </DataTable.ExpandedRow.Summary>
          <DataTable.ExpandedRow.Details>
            <FileDetails rowIndex={rowIndex} file={file} />
          </DataTable.ExpandedRow.Details>
        </DataTable.ExpandedRow>
      </Tooltip>
    </>
  );
};

export default FileRow;
